import { Spacer } from "@hackthenorth/north";
import { Link, Text } from "north.js";
import React, { useMemo } from "react";
import styled from "styled-components";

import { Header, PageWrapper, Spinner } from "src/shared/components";
import { Route } from "src/shared/constants/route";
import { useEventContext } from "src/shared/contexts";
import { mediaQueries } from "src/theme/deviceBreakpoints";
import globalConstants from "src/theme/globalConstants";

import { getRandomHardwareLoadingMessage } from "../inventory/shared/loadingTextUtil";
import { useGetHardwareInfo } from "../shared/useGetHardwareInfo";

import CategoryItem from "./CategoryItem";

const secondaryTextStyle = {
  color: globalConstants?.color?.textSecondary as string,
};

const HardwareHome: React.FC = () => {
  const { hardware } = useEventContext();
  const takingRequests = hardware.takingRequests;
  const { hardwareCategories, hardwareCategoriesDataLoading } =
    useGetHardwareInfo();

  // Loading message for spinner
  const loadingMessage = useMemo(() => {
    return getRandomHardwareLoadingMessage();
  }, []);

  if (hardwareCategoriesDataLoading) {
    return <Spinner text={loadingMessage} />;
  }

  return (
    <>
      <Header
        title="Hardware"
        subtitle={
          takingRequests
            ? "Browse and request a variety of hardware to bring your project to life!"
            : "Browse our hardware, but note that requests are currently closed!"
        }
      />
      <PageWrapper>
        <TopSection>
          <TopSectionText>
            <Text type="h2" style={{ marginBottom: "12px" }}>
              Welcome to Hardware Tool!
            </Text>
            <Text type="body1" style={secondaryTextStyle}>
              We’re excited to bring back hardware loans for Hack the North
              2026! If you need hardware for your project, please submit your
              request through this tool. Please note that images might not
              exactly match all stock. Once your items are ready, you can pick
              them up from the <b>QNX Makerspace</b>, located in{" "}
              <b>1427/1437 on Floor 1 of PSE (formerly E7).</b>
            </Text>
            <Text type="body1" style={secondaryTextStyle}>
              Before submitting a request, we strongly recommend taking a quick
              look through the <Link href={Route.HARDWARE_FAQ}>FAQ</Link>. It
              covers the checkout and return process, along with other helpful
              information about borrowing hardware during the event.
            </Text>
            <Text type="body1" style={secondaryTextStyle}>
              Requests will open at <b>10 PM on Friday</b>, and hardware
              checkout will be available during the following times:
            </Text>
            <ul style={{ margin: 0, paddingLeft: "24px" }}>
              <li>
                <Text type="body1" style={secondaryTextStyle}>
                  Saturday, 12 AM – 3:30 AM — requests close at 3:30 AM
                </Text>
              </li>
              <li>
                <Text type="body1" style={secondaryTextStyle}>
                  Saturday, 9 AM – Sunday, 2 AM — requests close at 1:30 AM
                </Text>
              </li>
            </ul>
            <Text type="body1" style={secondaryTextStyle}>
              If you have any questions, need help choosing hardware, or run
              into an issue with something you borrowed, feel free to reach out
              to <b>@Julian BK (Hack the North)</b> on Slack or come by the
              hardware desk — we’re happy to help.
            </Text>
            <Text type="body1" style={secondaryTextStyle}>
              If an item you receive is defective or gets damaged, please bring
              it back to the hardware desk and let us know. We may be able to
              provide a replacement, and reporting broken hardware helps us make
              sure it doesn’t get handed out to someone else. You won’t be
              charged for accidental damage unless the item was clearly damaged
              through misuse.
            </Text>
            <Text type="body1" style={secondaryTextStyle}>
              We can’t wait to see what you build!
            </Text>
          </TopSectionText>
        </TopSection>
        {hardwareCategories.length === 0 ? (
          <Text
            type="body1"
            style={{ color: globalConstants?.color?.textSecondary as string }}
          >
            No items match your search.
          </Text>
        ) : (
          <>
            <TopSection>
              <TopSectionText>
                <Text type="h3" aria-setsize={20}>
                  All categories
                </Text>
                <Text
                  type="body1"
                  style={{
                    color: globalConstants?.color?.textSecondary as string,
                  }}
                >
                  Check out all of our items below!
                </Text>
              </TopSectionText>
            </TopSection>
            <GridLayout>
              {hardwareCategories.map((category) => (
                <CategoryItem
                  key={category.id}
                  category={category.name}
                  image={category.image}
                  categoryId={category.id}
                />
              ))}
            </GridLayout>
          </>
        )}
        <Spacer height={48} />
      </PageWrapper>
    </>
  );
};

const TopSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  margin-bottom: 48px;
`;

const TopSectionText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const GridLayout = styled.div`
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 48px 24px;

  ${mediaQueries.extraLarge} {
    grid-template-columns: repeat(4, 1fr);
  }

  ${mediaQueries.medium} {
    grid-template-columns: repeat(3, 1fr);
  }

  ${mediaQueries.tablet} {
    grid-template-columns: repeat(2, 1fr);
  }

  ${mediaQueries.mobile} {
    grid-template-columns: repeat(1, 1fr);
    justify-items: center;
  }
`;

export default HardwareHome;
